// This page renders a custom Leg 1 (reverse calc) and a standard GSec form for Leg 2
import React, { useEffect, useMemo, useState, useCallback } from 'react';
// import FixedIncomeGsecPage from './FixedIncomeGsecPage';
import { faceValueFromSettlement, pricePer100FromYield, yieldFromFaceValueAndSettlement } from '../utils/bondPricing';
import axios from 'axios';
import { API_BASE_URL, getAuthHeaders, getCurrentUserId } from '../services/api';
import { validateAvailableBalance } from '../utils/balanceValidation';
import GsecSellDealModal from '../components/GsecSellDealModal';
import { checkHolidayForCurrency, formatHolidayMessage } from '../utils/holidayValidation';
import { DATE_INPUT_MIN, DATE_INPUT_MAX, isDateInRange } from '../utils/dateBounds';
import DocumentUpload from '../components/DocumentUpload';
import DocumentList from '../components/DocumentList';
import RecentDealsDateFilter from '../components/RecentDealsDateFilter';
import { useRecentDealsFilterDate } from '../hooks/useRecentDealsFilterDate';
import { filterDealsByFilterDate } from '../utils/recentDealsDate';

const initialFormState = {
  tradeDate: '',
  tradeType: '',
  transactionType: '',
  counterparty: '',
  dealNumber: '',
  isin: '',
  faceValue: '',
  adjustedFaceValue: '',
  faceValueAdjustment: '',
  valueDate: '',
  nextCouponDate: '',
  lastCouponDate: '',
  numberOfDaysInterestAccrued: '',
  numberOfDaysForCouponPeriod: '',
  accruedInterest: '',
  accruedInterestBase: '',
  couponInterest: '',
  cleanPrice: '',
  dirtyPrice: '',
  couponDate1: '',
  couponDate2: '',
  issueDate: '',
  maturityDate: '',
  couponRate: '',
  yieldRate: '',
  cleanPriceBase: '',
  accruedInterestCalculation: '',
  broker: '',
  accruedInterestSixDecimals: '',
  accruedInterestFor100: '',
  settlementAmount: '',
  settlementMode: '',
  // Default trading defaults requested by ops: fund movement Yes,
  // Sherwood portfolio + Sherwood (trading) strategy, Seylan Bank custodian.
  // Settlement mode is auto-picked after the settlement-modes list loads
  // (composite value depends on the live payment-master payload).
  fundMovement: 'yes',
  couponDates: '',
  yield: '',
  brokerage: '',
  currency: 'LKR',
  portfolio: 'Sherwood',
  strategy: 'Sherwood',
  accruedInterestAdjustment: '',
  cleanPriceAdjustment: '',
  custodian: 'Seylan Bank',
  interestRate: '',
  accruedInterestPer100: '',
  // Additional fields needed for Leg 2 calculations
  issueDate: '',
  maturityDate: '',
  couponDate1: '',
  couponDate2: '',
  couponRate: ''
};

/** Senior/GSEC convention: money accrued = (per 100) × face ÷ 100 + adjustment (LKR). */
function accruedInterestMoneyFromPer100(per100, faceValue, adjustmentMoney = 0) {
  const per = parseFloat(per100);
  const fv = parseFloat(String(faceValue || '').replace(/,/g, ''));
  if (!Number.isFinite(per) || !Number.isFinite(fv) || fv <= 0) {
    return { base: '', money: '' };
  }
  const base = (Math.round(((per * fv) / 100) * 1000000) / 1000000).toFixed(6);
  const adj = parseFloat(adjustmentMoney) || 0;
  const money = (Math.round((parseFloat(base) + adj) * 10000) / 10000).toFixed(4);
  return { base, money };
}

function withAccruedInterestMoney(leg) {
  const fv = leg.adjustedFaceValue || leg.faceValue;
  const { base, money } = accruedInterestMoneyFromPer100(
    leg.accruedInterestPer100,
    fv,
    leg.accruedInterestAdjustment
  );
  if (!money) return leg;
  return {
    ...leg,
    accruedInterestBase: base,
    accruedInterest: money,
    accruedInterestFor100: base
  };
}

const FixedIncomeBuyBackPage = (props) => {
  const [formLeg1, setFormLeg1] = useState(initialFormState);
  const [formLeg2, setFormLeg2] = useState(initialFormState);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [lastModifiedField, setLastModifiedField] = useState(null); // Track which field was last modified
  const [dayCount, setDayCount] = useState('364'); // Day count for interest calculation (365 or 364)
  const [leg1TradeDateHolidayError, setLeg1TradeDateHolidayError] = useState('');
  const [leg1ValueDateHolidayError, setLeg1ValueDateHolidayError] = useState('');
  const [leg2ValueDateHolidayError, setLeg2ValueDateHolidayError] = useState('');
  
  // State for GSEC report data and balance validation
  const [gsecReportData, setGsecReportData] = useState([]);
  const [balanceValidation, setBalanceValidation] = useState({
    isValid: true,
    message: ''
  });

  // State for sell modal
  const [showSellModal, setShowSellModal] = useState(false);
  const [sellDeals, setSellDeals] = useState([]);
  const [createdBuyBackDealId, setCreatedBuyBackDealId] = useState(null);
  const [showBuyBackDocumentSection, setShowBuyBackDocumentSection] = useState(false);
  const [selectedBuyBackDealForDocuments, setSelectedBuyBackDealForDocuments] = useState(null);
  const [portfolioFromSellModal, setPortfolioFromSellModal] = useState(false);
  const [isBuybackViewMode, setIsBuybackViewMode] = useState(false);
  const [isBuybackEditMode, setIsBuybackEditMode] = useState(false);
  const [editingBuybackDealId, setEditingBuybackDealId] = useState(null);
  const [editingBuybackDealStatus, setEditingBuybackDealStatus] = useState('');

  // Helper function to calculate days between two dates
  const calculateDaysBetween = (date1, date2) => {
    if (!date1 || !date2) return 0;
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    const diffTime = Math.abs(d2 - d1);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Brokerage is computed as: fee amount = faceValue * 0.015%
  // We store the fee amount (not the percentage), rounded to 4 decimals.
  const BROKERAGE_RATE = 0.00015; // 0.015%

  const computeBrokerageFeeAmount = (faceValue) => {
    const raw = faceValue ?? '';
    const normalized = String(raw).replace(/,/g, '');
    const n = parseFloat(normalized);
    if (!isFinite(n) || n <= 0) return '';
    return (n * BROKERAGE_RATE).toFixed(4);
  };

  const normalizeAmountInput = (value) => {
    if (value === null || value === undefined) return '';
    const raw = String(value).replace(/,/g, '').replace(/[^\d.]/g, '');
    const parts = raw.split('.');
    if (parts.length <= 1) return parts[0];
    return `${parts[0]}.${parts.slice(1).join('')}`;
  };

  const normalizeSignedAmountInput = (value) => {
    if (value === null || value === undefined) return '';
    const raw = String(value).replace(/,/g, '').trim();
    const sign = raw.startsWith('-') ? '-' : raw.startsWith('+') ? '+' : '';
    const unsigned = raw.replace(/^[+-]/, '').replace(/[^\d.]/g, '');
    const parts = unsigned.split('.');
    const normalizedUnsigned = parts.length <= 1 ? parts[0] : `${parts[0]}.${parts.slice(1).join('')}`;
    return `${sign}${normalizedUnsigned}`;
  };

  const formatAmountInput = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const normalized = String(value).replace(/,/g, '');
    if (normalized === '') return '';

    const [integerPartRaw, decimalPart] = normalized.split('.');
    const integerPart = integerPartRaw === '' ? '0' : integerPartRaw;
    const withCommas = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return decimalPart !== undefined ? `${withCommas}.${decimalPart}` : withCommas;
  };

  // Tint auto-calculated / read-only placeholders based on transaction type.
  // Buy → soft green, Sell → soft red, unset → neutral.
  const getDerivedFieldStyle = (txnType) => {
    if (txnType === 'Buy') {
      return {
        backgroundColor: '#dcfce7',
        borderColor: '#16a34a',
        color: '#14532d',
        transition: 'background-color 0.25s ease, border-color 0.25s ease, color 0.25s ease',
      };
    }
    if (txnType === 'Sell') {
      return {
        backgroundColor: '#fee2e2',
        borderColor: '#dc2626',
        color: '#7f1d1d',
        transition: 'background-color 0.25s ease, border-color 0.25s ease, color 0.25s ease',
      };
    }
    return {
      transition: 'background-color 0.25s ease, border-color 0.25s ease, color 0.25s ease',
    };
  };

  // Calculate yield from settlement amount and face value
  const calculateYieldFromSettlement = (settlementAmount, faceValue) => {
    if (!settlementAmount || !faceValue || parseFloat(settlementAmount) <= 0 || parseFloat(faceValue) <= 0) {
      return '';
    }
    
    const settlement = parseFloat(settlementAmount);
    const face = parseFloat(faceValue);
    
    if (isNaN(settlement) || isNaN(face) || face === 0) {
      return '';
    }
    
    // Calculate yield as percentage: ((Settlement Amount - Face Value) / Face Value) * 100
    const yieldValue = ((settlement - face) / face) * 100;
    
    // Return with 4 decimal places
    return yieldValue.toFixed(4);
  };

  // Calculate dirty price from settlement amount and face value (per 100 basis)
  const calculateDirtyPriceFromSettlement = (settlementAmount, faceValue) => {
    if (!settlementAmount || !faceValue || parseFloat(settlementAmount) <= 0 || parseFloat(faceValue) <= 0) {
      return '';
    }
    
    const settlement = parseFloat(settlementAmount);
    const face = parseFloat(faceValue);
    
    if (isNaN(settlement) || isNaN(face) || face === 0) {
      return '';
    }
    
    // Dirty price per 100 = (Settlement Amount / Face Value) * 100
    const dirtyPricePer100 = (settlement / face) * 100;
    
    // Return with 4 decimal places
    return dirtyPricePer100.toFixed(4);
  };

  // Calculate accrued interest per 100
  const calculateAccruedInterestPer100 = (couponRate, valueDate, issueDate, couponDate1, couponDate2) => {
    if (!couponRate || !valueDate || !issueDate || !couponDate1 || !couponDate2) {
      return '';
    }

    const cr = parseFloat(couponRate) / 100; // Convert percentage to decimal
    const settle = new Date(valueDate);
    const issue = new Date(issueDate);
    const frequency = 2; // Semi-annual

    // Calculate coupon amount per period (per 100)
    const couponPer100 = 100 * cr / frequency;
    const monthsPerPeriod = 12 / frequency; // 6 months for semi-annual

    // Find last coupon date
    let lastCoupon = new Date(issue);
    while (lastCoupon <= settle) {
      lastCoupon.setMonth(lastCoupon.getMonth() + monthsPerPeriod);
    }
    lastCoupon.setMonth(lastCoupon.getMonth() - monthsPerPeriod);

    // Create next coupon date
    const nextCoupon = new Date(lastCoupon);
    nextCoupon.setMonth(nextCoupon.getMonth() + monthsPerPeriod);

    // Calculate days in coupon period and days accrued
    const daysInPeriod = calculateDaysBetween(nextCoupon, lastCoupon);
    const daysAccrued = calculateDaysBetween(settle, lastCoupon);

    if (daysInPeriod === 0) {
      return '';
    }

    // Calculate accrued interest per 100
    const accruedInterestPer100 = couponPer100 * (daysAccrued / daysInPeriod);
    
    // Round to 4 decimal places (5th decimal >= 5 rounds up, < 5 stays same)
    return (Math.round(accruedInterestPer100 * 10000) / 10000).toFixed(4);
  };

  // Calculate clean price (dirty price - accrued interest)
  const calculateCleanPrice = (dirtyPricePer100, accruedInterestPer100) => {
    if (!dirtyPricePer100 || !accruedInterestPer100) {
      return '';
    }

    const dirty = parseFloat(dirtyPricePer100);
    const accrued = parseFloat(accruedInterestPer100);

    if (isNaN(dirty) || isNaN(accrued)) {
      return '';
    }

    const cleanPrice = dirty - accrued;
    
    // Round to 4 decimal places (5th decimal >= 5 rounds up, < 5 stays same)
    return (Math.round(cleanPrice * 10000) / 10000).toFixed(4);
  };

  // Calculate dirty price (clean price + accrued interest)
  const calculateDirtyPriceFromCleanAndAccrued = (cleanPricePer100, accruedInterestPer100) => {
    if (!cleanPricePer100 || !accruedInterestPer100) {
      return '';
    }

    const clean = parseFloat(cleanPricePer100);
    const accrued = parseFloat(accruedInterestPer100);

    if (isNaN(clean) || isNaN(accrued)) {
      return '';
    }

    const dirtyPrice = clean + accrued;
    
    // Round to 4 decimal places (5th decimal >= 5 rounds up, < 5 stays same)
    return (Math.round(dirtyPrice * 10000) / 10000).toFixed(4);
  };

  // Back-calculate yield from clean price using bond pricing formula
  const calculateYieldFromCleanPrice = (cleanPrice, couponRate, valueDate, maturityDate, issueDate, couponDate1, couponDate2) => {
    if (!cleanPrice || !couponRate || !valueDate || !maturityDate || !issueDate || !couponDate1 || !couponDate2) {
      return '';
    }

    const clean = parseFloat(cleanPrice);
    const cr = parseFloat(couponRate) / 100;
    const settle = new Date(valueDate);
    const issue = new Date(issueDate);
    const maturity = new Date(maturityDate);
    const frequency = 2; // Semi-annual

    // Calculate coupon amount per period (per 100)
    const couponPer100 = 100 * cr / frequency;
    const monthsPerPeriod = 12 / frequency;

    // Find last coupon date
    let lastCoupon = new Date(issue);
    while (lastCoupon <= settle) {
      lastCoupon.setMonth(lastCoupon.getMonth() + monthsPerPeriod);
    }
    lastCoupon.setMonth(lastCoupon.getMonth() - monthsPerPeriod);

    // Create next coupon date
    const nextCoupon = new Date(lastCoupon);
    nextCoupon.setMonth(nextCoupon.getMonth() + monthsPerPeriod);

    // Calculate days in coupon period and days to next coupon
    const daysInPeriod = calculateDaysBetween(nextCoupon, lastCoupon);
    const daysToNextCoupon = calculateDaysBetween(nextCoupon, settle);
    const fractionalPeriod = daysToNextCoupon / daysInPeriod;

    // Generate cash flows
    const cashFlows = [];
    let cfDate = new Date(nextCoupon);
    let periodCount = 0;

    while (cfDate <= maturity) {
      const isFinal = cfDate.getTime() === maturity.getTime();
      const amount = isFinal ? couponPer100 + 100 : couponPer100;
      cashFlows.push({
        date: new Date(cfDate),
        amount,
        periodCount
      });
      cfDate.setMonth(cfDate.getMonth() + monthsPerPeriod);
      periodCount++;
    }

    // Use Newton-Raphson method to find yield
    let ytm = 0.05; // Initial guess (5%)
    const tolerance = 0.0001;
    const maxIterations = 100;

    for (let i = 0; i < maxIterations; i++) {
      const ytmPerPeriod = ytm / frequency;
      
      // Calculate present value of cash flows
      let pv = 0;
      for (const cf of cashFlows) {
        const t = fractionalPeriod + cf.periodCount;
        pv += cf.amount / Math.pow(1 + ytmPerPeriod, t);
      }

      // Calculate dirty price
      const dirtyPrice = pv;
      
      // Calculate accrued interest
      const daysAccrued = calculateDaysBetween(settle, lastCoupon);
      const accruedInterest = couponPer100 * (daysAccrued / daysInPeriod);
      
      // Calculate clean price
      const calculatedCleanPrice = dirtyPrice - accruedInterest;
      
      // Check if we've found the solution
      const difference = calculatedCleanPrice - clean;
      if (Math.abs(difference) < tolerance) {
        break;
      }

      // Calculate derivative for Newton-Raphson
      let derivative = 0;
      for (const cf of cashFlows) {
        const t = fractionalPeriod + cf.periodCount;
        derivative -= (cf.amount * t) / (Math.pow(1 + ytmPerPeriod, t + 1) * frequency);
      }

      // Update yield estimate
      if (Math.abs(derivative) > 0.0001) {
        ytm = ytm - difference / derivative;
      } else {
        break;
      }
    }

    // Return yield as percentage with 4 decimal places
    return (ytm * 100).toFixed(4);
  };

  // Recent deals blotter state
  const [recentDeals, setRecentDeals] = useState([]);
  const { filterDateYmd: recentDealsFilterDate, setFilterDateYmd: setRecentDealsFilterDate, loading: recentDealsFilterLoading } = useRecentDealsFilterDate();
  const [loadingDeals, setLoadingDeals] = useState(false);
  const [showBlotter, setShowBlotter] = useState(false);

  // ISIN master for Leg 1
  const [isinList, setIsinList] = useState([]);
  const [loadingIsins, setLoadingIsins] = useState(false);
  const memoIsins = useMemo(() => Array.isArray(isinList) ? isinList : [], [isinList]);

  // Additional data for dropdowns
  const [counterparties, setCounterparties] = useState([]);
  const [brokers, setBrokers] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [custodians, setCustodians] = useState([]);
  const [settlementModes, setSettlementModes] = useState([]);

  // Loading states
  const [loadingCounterparties, setLoadingCounterparties] = useState(false);
  const [loadingBrokers, setLoadingBrokers] = useState(false);
  const [loadingPortfolios, setLoadingPortfolios] = useState(false);
  const [loadingStrategies, setLoadingStrategies] = useState(false);

  useEffect(() => {
    setLoadingIsins(true);
    axios.get(`${API_BASE_URL}/isin-master`)
      .then(res => setIsinList(res.data?.data || []))
      .catch(() => setIsinList([]))
      .finally(() => setLoadingIsins(false));
  }, []);

  // Fetch counterparties
  useEffect(() => {
    setLoadingCounterparties(true);
    axios.get(`${API_BASE_URL}/counterparties`)
      .then(res => setCounterparties(res.data?.data || []))
      .catch(() => setCounterparties([]))
      .finally(() => setLoadingCounterparties(false));
  }, []);

  // Fetch brokers
  useEffect(() => {
    setLoadingBrokers(true);
    axios.get(`${API_BASE_URL}/brokers`)
      .then(res => setBrokers(res.data?.data || res.data || []))
      .catch(() => setBrokers([]))
      .finally(() => setLoadingBrokers(false));
  }, []);

  // Fetch portfolios
  useEffect(() => {
    setLoadingPortfolios(true);
    axios.get(`${API_BASE_URL}/portfolio-master`)
      .then(res => setPortfolios(res.data || []))
      .catch(() => setPortfolios([]))
      .finally(() => setLoadingPortfolios(false));
  }, []);

  // Fetch strategies
  useEffect(() => {
    setLoadingStrategies(true);
    axios.get(`${API_BASE_URL}/strategy-master`)
      .then(res => setStrategies(res.data || []))
      .catch(() => setStrategies([]))
      .finally(() => setLoadingStrategies(false));
  }, []);

  // Set default values for some fields
  useEffect(() => {
    const today = new Date();
    const leg2Date = new Date();
    leg2Date.setDate(today.getDate() + 30); // Set Leg 2 to 30 days later for interest calculation
    
    // Seed dates only. We deliberately do NOT clear settlementMode/portfolio/
    // strategy/custodian/fundMovement here so the initialFormState defaults
    // (and the settlement-mode auto-pick below) take effect.
    setFormLeg1(prev => ({
      ...prev,
      tradeDate: today.toISOString().split('T')[0],
      valueDate: today.toISOString().split('T')[0],
      tradeType: 'BuyBack'
    }));
    setFormLeg2(prev => ({
      ...prev,
      valueDate: leg2Date.toISOString().split('T')[0], // 30 days later for interest calculation
      tradeType: 'BuyBack'
    }));
  }, []);

  // Set custodian options
  useEffect(() => {
    setCustodians([
      'Sampath Bank',
      'Seylan Bank', 
      'DFCC Bank',
      'Pan Asia Bank',
      'Bank of Ceylon',
      'Commercial Bank',
      'NDB Bank',
      'Hatton National Bank',
      'Nations Trust Bank'
    ]);
  }, []);

  // Auto-pick default Settlement Mode (Seylan Bank) once the live list loads.
  // The dropdown values are composite strings, so we match by label substring.
  useEffect(() => {
    if (!settlementModes || settlementModes.length === 0) return;
    const pick = settlementModes.find(
      (m) => (m.label || '').toLowerCase().includes('seylan bank')
    );
    if (!pick) return;
    setFormLeg1((prev) => (prev.settlementMode ? prev : { ...prev, settlementMode: pick.value }));
    setFormLeg2((prev) => (prev.settlementMode ? prev : { ...prev, settlementMode: pick.value }));
  }, [settlementModes]);

  // Safety net: if portfolios/strategies master lists don't contain a literal
  // "Sherwood" id, but DO contain an option whose name/type matches, pick it.
  useEffect(() => {
    if (!portfolios || portfolios.length === 0) return;
    const hasLiteralSherwood = portfolios.some((p) => String(p.portfolio_id) === 'Sherwood');
    if (hasLiteralSherwood) return; // initialFormState default is fine
    const match = portfolios.find(
      (p) => `${p.portfolio_id || ''} ${p.portfolio_name || ''}`.toLowerCase().includes('sherwood')
    );
    if (!match) return;
    setFormLeg1((prev) => (prev.portfolio && prev.portfolio !== 'Sherwood' ? prev : { ...prev, portfolio: match.portfolio_id }));
    setFormLeg2((prev) => (prev.portfolio && prev.portfolio !== 'Sherwood' ? prev : { ...prev, portfolio: match.portfolio_id }));
  }, [portfolios]);

  useEffect(() => {
    if (!strategies || strategies.length === 0) return;
    const hasLiteralSherwood = strategies.some((s) => String(s.strategy_id) === 'Sherwood');
    if (hasLiteralSherwood) return; // initialFormState default is fine
    const match = strategies.find(
      (s) => `${s.strategy_id || ''} ${s.strategy_type || ''}`.toLowerCase().includes('sherwood')
    );
    if (!match) return;
    setFormLeg1((prev) => (prev.strategy && prev.strategy !== 'Sherwood' ? prev : { ...prev, strategy: match.strategy_id }));
    setFormLeg2((prev) => (prev.strategy && prev.strategy !== 'Sherwood' ? prev : { ...prev, strategy: match.strategy_id }));
  }, [strategies]);

  // Set settlement mode options
  useEffect(() => {
    const fetchSettlementModes = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/payment-master`);
        const items = res?.data?.data || res?.data || [];

        const mapped = (Array.isArray(items) ? items : [])
          .map((m) => {
            const owner =
              m.payment_method_owner ||
              m.owner ||
              m.bank_owner ||
              m.bank_name ||
              m.bank ||
              m.payment_owner ||
              '';

            const paymentMethod =
              m.payment_method ||
              m.method ||
              m.PaymentMethod ||
              '';

            const paymentMethodCode =
              m.payment_method_code ||
              m.paymentMethodCode ||
              m.code ||
              '';

            if (!paymentMethod) return null;

            // Use composite dropdown value so we don't collapse multiple owners
            // that share the same payment_method_code.
            const value = `${paymentMethodCode || paymentMethod}|${owner || ''}|${paymentMethod}`;
            const label = owner
              ? `${owner} - ${paymentMethod}${paymentMethodCode ? ` (${paymentMethodCode})` : ''}`
              : `${paymentMethod}${paymentMethodCode ? ` (${paymentMethodCode})` : ''}`;

            return { value, label, paymentMethod };
          })
          .filter(Boolean);

        // De-duplicate by dropdown value (payment_method_code when present)
        const unique = [];
        const seen = new Set();
        for (const opt of mapped) {
          if (seen.has(opt.value)) continue;
          seen.add(opt.value);
          unique.push(opt);
        }

        if (unique.length > 0) {
          setSettlementModes(unique);
          return;
        }

        // Fallback if API returns unexpected shape
        setSettlementModes([
          { value: 'RTGS', label: 'RTGS', paymentMethod: 'RTGS' },
          { value: 'CEFT', label: 'CEFT', paymentMethod: 'CEFT' },
          { value: 'SLIPS', label: 'SLIPS', paymentMethod: 'SLIPS' },
          { value: 'Cheque', label: 'Cheque', paymentMethod: 'Cheque' },
          { value: 'Other', label: 'Other', paymentMethod: 'Other' }
        ]);
      } catch (e) {
        console.error('Error fetching live settlement modes:', e);
        setSettlementModes([
          { value: 'RTGS', label: 'RTGS', paymentMethod: 'RTGS' },
          { value: 'CEFT', label: 'CEFT', paymentMethod: 'CEFT' },
          { value: 'SLIPS', label: 'SLIPS', paymentMethod: 'SLIPS' },
          { value: 'Cheque', label: 'Cheque', paymentMethod: 'Cheque' },
          { value: 'Other', label: 'Other', paymentMethod: 'Other' }
        ]);
      }
    };

    fetchSettlementModes();
  }, []);

  // Ensure we set valid defaults once settlement modes are loaded.
  useEffect(() => {
    if (!settlementModes || settlementModes.length === 0) return;
    const first = settlementModes[0]?.value;
    if (!first) return;

    setFormLeg1((prev) => {
      const currentVal = prev?.settlementMode;
      const valid = settlementModes.some((m) => m.value === currentVal);
      return { ...prev, settlementMode: valid ? currentVal : first };
    });
    setFormLeg2((prev) => {
      const currentVal = prev?.settlementMode;
      const valid = settlementModes.some((m) => m.value === currentVal);
      return { ...prev, settlementMode: valid ? currentVal : first };
    });
  }, [settlementModes]);

  // Sync Leg 2 with Leg 1
  useEffect(() => {
    // Consolidated: Sync all Leg 1 data to Leg 2 AND perform calculations
    console.log('Syncing Leg 1 data to Leg 2 and calculating. Leg 1 data:', {
      faceValue: formLeg1.faceValue,
      settlementAmount: formLeg1.settlementAmount,
      interestRate: formLeg1.interestRate,
      issueDate: formLeg1.issueDate,
      maturityDate: formLeg1.maturityDate,
      couponDate1: formLeg1.couponDate1,
      couponDate2: formLeg1.couponDate2,
      couponRate: formLeg1.couponRate,
      valueDate: formLeg1.valueDate,
      transactionType: formLeg1.transactionType
    });

    // First, sync all data from Leg 1 to Leg 2 (except valueDate - let user control it)
    const updatedLeg2 = {
      ...formLeg2,
      isin: formLeg1.isin,
      // Broker is chosen only once in the UI (Leg 1). Keep Leg 2 in sync
      // so brokerage auto-calculation can work for both legs.
      broker: formLeg1.broker,
      issueDate: formLeg1.issueDate,
      maturityDate: formLeg1.maturityDate,
      couponDate1: formLeg1.couponDate1,
      couponDate2: formLeg1.couponDate2,
      couponRate: formLeg1.couponRate,
      // valueDate: formLeg1.valueDate, // REMOVED - let user control Leg 2 value date
      faceValue: formLeg1.faceValue,
      adjustedFaceValue: formLeg1.adjustedFaceValue || formLeg1.faceValue,
      faceValueAdjustment: formLeg1.faceValueAdjustment || '',
      // Auto-populate trade date from Leg 1
      tradeDate: formLeg1.tradeDate,
      // Auto-switch transaction type: if Leg 1 is Buy, Leg 2 is Sell, and vice versa
      transactionType: formLeg1.transactionType === 'Buy' ? 'Sell' : 'Buy'
    };

    // Calculate Leg 2 settlement amount using date difference interest calculation
    const sa = parseFloat(formLeg1.settlementAmount);
    const rate = parseFloat(formLeg1.interestRate);
    const leg1ValueDate = formLeg1.valueDate;
    const leg2ValueDate = updatedLeg2.valueDate;
    
    if (!isNaN(sa) && isFinite(sa) && !isNaN(rate) && isFinite(rate) && leg1ValueDate && leg2ValueDate) {
      // Calculate number of days between Leg 1 and Leg 2 value dates
      const daysBetween = calculateDaysBetween(leg1ValueDate, leg2ValueDate);
      
      // Calculate interest: Settlement Amount × Interest Rate × (Days / dayCount)
      const interest = sa * (rate / 100) * (daysBetween / parseInt(dayCount));
      
      // Add interest to settlement amount
      const totalSettlement = sa + interest;
      
      updatedLeg2.settlementAmount = totalSettlement.toFixed(2);
      
      // Calculate comprehensive bond pricing for Leg 2
      // 1. Calculate dirty price from settlement amount and face value
      const dirtyPricePer100 = calculateDirtyPriceFromSettlement(totalSettlement.toFixed(2), formLeg1.faceValue);
      if (dirtyPricePer100) {
        updatedLeg2.dirtyPrice = dirtyPricePer100;
      }

      // 2. Calculate accrued interest per 100 (rounded to 4th decimal)
      const accruedInterestPer100 = calculateAccruedInterestPer100(
        formLeg1.couponRate, 
        leg2ValueDate, 
        formLeg1.issueDate, 
        formLeg1.couponDate1, 
        formLeg1.couponDate2
      );
      if (accruedInterestPer100) {
        updatedLeg2.accruedInterestPer100 = accruedInterestPer100;
      }

      // 3. Calculate clean price (dirty price - accrued interest)
      const cleanPrice = calculateCleanPrice(dirtyPricePer100, accruedInterestPer100);
      if (cleanPrice) {
        updatedLeg2.cleanPrice = cleanPrice;
      }

      // 4. Recalculate dirty price (clean price + accrued interest) to ensure consistency
      const recalculatedDirtyPrice = calculateDirtyPriceFromCleanAndAccrued(cleanPrice, accruedInterestPer100);
      if (recalculatedDirtyPrice) {
        updatedLeg2.dirtyPrice = recalculatedDirtyPrice;
      }

      // 4. Back-calculate yield from clean price using bond pricing formula
      const calculatedYield = calculateYieldFromCleanPrice(
        cleanPrice,
        formLeg1.couponRate,
        leg2ValueDate,
        formLeg1.maturityDate,
        formLeg1.issueDate,
        formLeg1.couponDate1,
        formLeg1.couponDate2
      );
      if (calculatedYield) {
        updatedLeg2.yieldRate = calculatedYield;
      }
      
      console.log('Comprehensive Leg 2 calculation:', {
        settlementAmount: sa,
        interestRate: rate,
        leg1ValueDate,
        leg2ValueDate,
        daysBetween,
        interest,
        totalSettlement,
        dirtyPricePer100,
        accruedInterestPer100,
        cleanPrice,
        calculatedYield
      });
    } else {
      updatedLeg2.settlementAmount = '';
    }

    // Now perform the forward calculation if we have all required data.
    // This supersedes the step 1-4 prices above with the bond-pricing-derived values.
    if (updatedLeg2.faceValue && updatedLeg2.settlementAmount && updatedLeg2.couponRate &&
        updatedLeg2.valueDate && updatedLeg2.issueDate && updatedLeg2.maturityDate &&
        updatedLeg2.couponDate1 && updatedLeg2.couponDate2) {

      console.log('Performing Leg 2 forward calculation with:', {
        faceValue: updatedLeg2.faceValue,
        settlementAmount: updatedLeg2.settlementAmount,
        couponRate: updatedLeg2.couponRate,
        valueDate: updatedLeg2.valueDate,
        issueDate: updatedLeg2.issueDate,
        maturityDate: updatedLeg2.maturityDate,
        couponDate1: updatedLeg2.couponDate1,
        couponDate2: updatedLeg2.couponDate2
      });

      const result = yieldFromFaceValueAndSettlement({
        faceValue: updatedLeg2.faceValue,
        settlementAmount: updatedLeg2.settlementAmount,
        couponRate: updatedLeg2.couponRate,
        valueDate: updatedLeg2.valueDate,
        issueDate: updatedLeg2.issueDate,
        maturityDate: updatedLeg2.maturityDate,
        couponDate1: updatedLeg2.couponDate1,
        couponDate2: updatedLeg2.couponDate2
      });

      console.log('Leg 2 calculation result:', result);

      if (!result.yieldRate || result.yieldRate === '') {
        console.error('Yield calculation failed - no yield returned');
      }

      updatedLeg2.yieldRate = result.yieldRate;
      updatedLeg2.dirtyPrice = result.dirtyPrice;
      updatedLeg2.cleanPrice = result.cleanPrice;
      updatedLeg2.accruedInterestPer100 = result.accruedInterestPer100;
    } else {
      // Coupon dates or other bond metadata missing — can't run the full forward calculation.
      // Only clear yield and accrued interest; keep dirty/clean prices that were already
      // derived from the settlement amount in steps 1-4 above so they are saved to the DB.
      updatedLeg2.yieldRate = '';
      updatedLeg2.accruedInterestPer100 = '';
      console.log('Leg 2 forward calculation skipped — missing coupon or bond metadata');
    }

    setFormLeg2(withAccruedInterestMoney(updatedLeg2));
  }, [formLeg1.isin, formLeg1.faceValue, formLeg1.adjustedFaceValue, formLeg1.faceValueAdjustment, formLeg1.settlementAmount, formLeg1.interestRate, formLeg1.issueDate, formLeg1.maturityDate, formLeg1.couponDate1, formLeg1.couponDate2, formLeg1.couponRate, formLeg1.valueDate, formLeg1.transactionType, dayCount]);

  // Auto-calculate brokerage fee amount (fee = faceValue * 0.015%) for each leg.
  // - When broker is selected: overwrite brokerage from the entered face value.
  // - When broker is cleared: reset brokerage.
  useEffect(() => {
    const brokerSelected = Boolean(formLeg1.broker);
    const nextBrokerage = brokerSelected ? computeBrokerageFeeAmount(formLeg1.faceValue) : '';
    if (String(formLeg1.brokerage ?? '') !== String(nextBrokerage)) {
      setFormLeg1(prev => ({ ...prev, brokerage: nextBrokerage }));
    }
  }, [formLeg1.broker, formLeg1.faceValue, formLeg1.brokerage]);

  useEffect(() => {
    const brokerSelected = Boolean(formLeg2.broker);
    const nextBrokerage = brokerSelected ? computeBrokerageFeeAmount(formLeg2.faceValue) : '';
    if (String(formLeg2.brokerage ?? '') !== String(nextBrokerage)) {
      setFormLeg2(prev => ({ ...prev, brokerage: nextBrokerage }));
    }
  }, [formLeg2.broker, formLeg2.faceValue, formLeg2.brokerage]);

  // Separate useEffect to handle interest calculation when Leg 2 value date changes
  useEffect(() => {
    if (formLeg1.settlementAmount && formLeg1.interestRate && formLeg1.valueDate && formLeg2.valueDate) {
      const sa = parseFloat(formLeg1.settlementAmount);
      const rate = parseFloat(formLeg1.interestRate);
      const leg1ValueDate = formLeg1.valueDate;
      const leg2ValueDate = formLeg2.valueDate;
      
      if (!isNaN(sa) && isFinite(sa) && !isNaN(rate) && isFinite(rate)) {
        // Calculate number of days between Leg 1 and Leg 2 value dates
        const daysBetween = calculateDaysBetween(leg1ValueDate, leg2ValueDate);
        
        // Calculate interest: Settlement Amount × Interest Rate × (Days / dayCount)
        const interest = sa * (rate / 100) * (daysBetween / parseInt(dayCount));
        
        // Add interest to settlement amount
        const totalSettlement = sa + interest;
        
        // Calculate comprehensive bond pricing for Leg 2
        // 1. Calculate dirty price from settlement amount and face value
        const dirtyPricePer100 = calculateDirtyPriceFromSettlement(totalSettlement.toFixed(2), formLeg1.faceValue);
        
        // 2. Calculate accrued interest per 100 (rounded to 4th decimal)
        const accruedInterestPer100 = calculateAccruedInterestPer100(
          formLeg1.couponRate, 
          leg2ValueDate, 
          formLeg1.issueDate, 
          formLeg1.couponDate1, 
          formLeg1.couponDate2
        );
        
        // 3. Calculate clean price (dirty price - accrued interest)
        const cleanPrice = calculateCleanPrice(dirtyPricePer100, accruedInterestPer100);
        
        // 4. Recalculate dirty price (clean price + accrued interest) to ensure consistency
        const recalculatedDirtyPrice = calculateDirtyPriceFromCleanAndAccrued(cleanPrice, accruedInterestPer100);
        
        // 4. Back-calculate yield from clean price using bond pricing formula
        const calculatedYield = calculateYieldFromCleanPrice(
          cleanPrice,
          formLeg1.couponRate,
          leg2ValueDate,
          formLeg1.maturityDate,
          formLeg1.issueDate,
          formLeg1.couponDate1,
          formLeg1.couponDate2
        );
        
        setFormLeg2((prev) =>
          withAccruedInterestMoney({
            ...prev,
            settlementAmount: totalSettlement.toFixed(2),
            dirtyPrice: recalculatedDirtyPrice || prev.dirtyPrice,
            accruedInterestPer100: accruedInterestPer100 || prev.accruedInterestPer100,
            cleanPrice: cleanPrice || prev.cleanPrice,
            yieldRate: calculatedYield || prev.yieldRate
          })
        );
        
        console.log('Comprehensive Leg 2 calculation (value date changed):', {
          settlementAmount: sa,
          interestRate: rate,
          leg1ValueDate,
          leg2ValueDate,
          daysBetween,
          interest,
          totalSettlement,
          dirtyPricePer100,
          accruedInterestPer100,
          cleanPrice,
          calculatedYield
        });
      }
    }
  }, [formLeg2.valueDate, formLeg1.settlementAmount, formLeg1.interestRate, formLeg1.valueDate, formLeg1.faceValue, formLeg1.couponRate, formLeg1.issueDate, formLeg1.maturityDate, formLeg1.couponDate1, formLeg1.couponDate2, dayCount]);

  // Fetch recent buyback deals
  const fetchRecentDeals = useCallback(async () => {
    setLoadingDeals(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/buyback`, { headers: getAuthHeaders() });
      if (response.data?.success) {
        const sortedDeals = (response.data.data || [])
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setRecentDeals(sortedDeals);
      } else {
        setRecentDeals([]);
      }
    } catch (error) {
      console.error('Error fetching recent deals:', error);
      setRecentDeals([]);
    } finally {
      setLoadingDeals(false);
    }
  }, []);

  // Fetch GSEC report data for balance validation
  const fetchGsecReportData = useCallback(async () => {
    try {
      const asAtDate = new Date().toISOString().split('T')[0];
      const response = await axios.get(`${API_BASE_URL}/reports/gsec`, { params: { asAtDate } });
      setGsecReportData(response.data?.data || []);
    } catch (err) {
      console.error('Failed to fetch GSEC report data:', err);
      setGsecReportData([]);
    }
  }, []);

  // Fetch recent deals on component mount
  useEffect(() => {
    fetchRecentDeals();
    fetchGsecReportData();
  }, [fetchRecentDeals, fetchGsecReportData]);

  const filteredRecentDeals = useMemo(
    () => filterDealsByFilterDate(recentDeals, recentDealsFilterDate),
    [recentDeals, recentDealsFilterDate]
  );

  const handleLeg1Change = (e) => {
    const { name, value } = e.target;
    // Reject out-of-range / malformed dates (e.g. a 5-digit year).
    if ((name === 'tradeDate' || name === 'valueDate') && !isDateInRange(value)) {
      return;
    }
    const normalizedValue =
      name === 'faceValueAdjustment'
        ? normalizeSignedAmountInput(value)
        : (name === 'settlementAmount' || name === 'faceValue' || name === 'adjustedFaceValue')
          ? normalizeAmountInput(value)
          : value;
    const next = { ...formLeg1, [name]: normalizedValue };
    if (name === 'faceValue') {
      const enteredFace = parseFloat(String(normalizedValue || '').replace(/,/g, ''));
      const adjustment = parseFloat(String(formLeg1.faceValueAdjustment || '').replace(/,/g, ''));
      if (Number.isFinite(enteredFace) && Number.isFinite(adjustment) && Math.abs(adjustment) > 0.0001) {
        // Face Value input shows adjusted figure; store base figure internally.
        next.faceValue = String(enteredFace - adjustment);
      }
    }

    // Holiday validation for tradeDate and valueDate
    if (name === 'tradeDate' || name === 'valueDate') {
      // Validate holiday asynchronously
      if (value) {
        const currency = formLeg1.currency || 'LKR';
        checkHolidayForCurrency(value, currency).then(holidayCheck => {
          if (holidayCheck.isHoliday) {
            const message = formatHolidayMessage(holidayCheck, name === 'tradeDate' ? 'Trade Date' : 'Value Date');
            if (name === 'tradeDate') {
              setLeg1TradeDateHolidayError(message);
            } else {
              setLeg1ValueDateHolidayError(message);
            }
          } else {
            if (name === 'tradeDate') {
              setLeg1TradeDateHolidayError('');
            } else {
              setLeg1ValueDateHolidayError('');
            }
          }
        });
      } else {
        // Clear error if date is cleared
        if (name === 'tradeDate') {
          setLeg1TradeDateHolidayError('');
        } else {
          setLeg1ValueDateHolidayError('');
        }
      }
    }

    // Re-validate dates when currency changes
    if (name === 'currency') {
      const currency = value || 'LKR';
      if (formLeg1.tradeDate) {
        checkHolidayForCurrency(formLeg1.tradeDate, currency).then(holidayCheck => {
          if (holidayCheck.isHoliday) {
            setLeg1TradeDateHolidayError(formatHolidayMessage(holidayCheck, 'Trade Date'));
          } else {
            setLeg1TradeDateHolidayError('');
          }
        });
      }
      if (formLeg1.valueDate) {
        checkHolidayForCurrency(formLeg1.valueDate, currency).then(holidayCheck => {
          if (holidayCheck.isHoliday) {
            setLeg1ValueDateHolidayError(formatHolidayMessage(holidayCheck, 'Value Date'));
          } else {
            setLeg1ValueDateHolidayError('');
          }
        });
      }
    }

    // If transactionType changes to 'Sell', open modal
    if (name === 'transactionType' && value === 'Sell') {
      if (!formLeg1.isin) {
        setError('Please select an ISIN before selling.');
        return;
      }
      setShowSellModal(true);
      setFormLeg1(prev => ({ ...prev, transactionType: value }));
      return;
    }

    // When ISIN changes, auto-fill read-only fields from ISIN (and force currency LKR)
    if (name === 'isin') {
      console.log('ISIN changed to:', value);
      const selected = memoIsins.find(i => i.isin_number === value);
      console.log('Selected ISIN data:', selected);
      if (selected) {
        let issueDate = '';
        if (selected.issue_date) {
          const d = new Date(selected.issue_date);
          if (!isNaN(d.getTime())) issueDate = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
        }
        let maturityDate = '';
        if (selected.maturity_date) {
          const d = new Date(selected.maturity_date);
          if (!isNaN(d.getTime())) maturityDate = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
        }
        let couponDate1 = '';
        if (selected.coupon_date_1) {
          const d = new Date(selected.coupon_date_1);
          couponDate1 = `${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
        }
        let couponDate2 = '';
        if (selected.coupon_date_2) {
          const d = new Date(selected.coupon_date_2);
          couponDate2 = `${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
        }
        next.issueDate = issueDate;
        next.maturityDate = maturityDate;
        next.couponDate1 = couponDate1;
        next.couponDate2 = couponDate2;
        next.couponRate = selected.coupon_rate || next.couponRate;
        next.currency = 'LKR';
        console.log('Updated form with ISIN data:', { issueDate, maturityDate, couponDate1, couponDate2, couponRate: next.couponRate });
      }
    }
    // First come first serve calculation logic
    if ((name === 'settlementAmount' || name === 'faceValue' || name === 'yieldRate') &&
        next.issueDate && next.maturityDate && next.couponDate1 && next.couponDate2 && next.valueDate && next.couponRate) {
      
      // Track which field was last modified
      if (name === 'settlementAmount' || name === 'faceValue') {
        setLastModifiedField(name);
      }
      
      // Determine calculation direction based on last modified field
      const shouldCalculateFromSettlement = lastModifiedField === 'settlementAmount' || 
                                          (lastModifiedField === null && next.settlementAmount && !next.faceValue);
      
      console.log('Calculation direction:', {
        lastModifiedField,
        shouldCalculateFromSettlement,
        settlementAmount: next.settlementAmount,
        faceValue: next.faceValue,
        yieldRate: next.yieldRate
      });
      
      if (shouldCalculateFromSettlement && next.settlementAmount && next.yieldRate) {
        // Calculate Face Value from Settlement Amount
        console.log('Calculating Face Value from Settlement Amount');
        const res = faceValueFromSettlement({
          settlementAmount: next.settlementAmount,
          couponRate: next.couponRate,
          yieldRate: next.yieldRate,
          valueDate: next.valueDate,
          issueDate: next.issueDate,
          maturityDate: next.maturityDate,
          couponDate1: next.couponDate1,
          couponDate2: next.couponDate2
        });
        console.log('Face Value calculation result:', res);
        next.faceValue = Number(res.faceValue).toString();
        next.dirtyPrice = res.dirtyPricePer100;
        next.cleanPrice = res.cleanPricePer100;
        
        // Override accrued interest with properly rounded calculation
        const roundedAccruedInterest = calculateAccruedInterestPer100(
          next.couponRate,
          next.valueDate,
          next.issueDate,
          next.couponDate1,
          next.couponDate2
        );
        next.accruedInterestPer100 = roundedAccruedInterest;
        
        // Recalculate dirty price with rounded accrued interest
        const recalculatedDirtyPrice = calculateDirtyPriceFromCleanAndAccrued(next.cleanPrice, roundedAccruedInterest);
        next.dirtyPrice = recalculatedDirtyPrice;
        
      } else if (!shouldCalculateFromSettlement && next.faceValue && next.yieldRate) {
        // Calculate Settlement Amount from Face Value
        console.log('Calculating Settlement Amount from Face Value');
        const res = pricePer100FromYield({
          couponRate: next.couponRate,
          yieldRate: next.yieldRate,
          valueDate: next.valueDate,
          issueDate: next.issueDate,
          maturityDate: next.maturityDate,
          couponDate1: next.couponDate1,
          couponDate2: next.couponDate2
        });
        console.log('Settlement Amount calculation result:', res);
        
        // Calculate settlement amount: Face Value * Dirty Price / 100
        const faceValueNum = parseFloat(next.faceValue);
        const dirtyPriceNum = parseFloat(res.dirtyPrice);
        if (!isNaN(faceValueNum) && !isNaN(dirtyPriceNum)) {
          next.settlementAmount = (faceValueNum * dirtyPriceNum / 100).toFixed(2);
        }
        
        next.dirtyPrice = res.dirtyPrice;
        next.cleanPrice = res.cleanPrice;
        
        // Override accrued interest with properly rounded calculation
        const roundedAccruedInterest = calculateAccruedInterestPer100(
          next.couponRate,
          next.valueDate,
          next.issueDate,
          next.couponDate1,
          next.couponDate2
        );
        next.accruedInterestPer100 = roundedAccruedInterest;
        
        // Recalculate dirty price with rounded accrued interest
        const recalculatedDirtyPrice = calculateDirtyPriceFromCleanAndAccrued(next.cleanPrice, roundedAccruedInterest);
        next.dirtyPrice = recalculatedDirtyPrice;
      }
    }
    const baseFaceNum = parseFloat(String(next.faceValue || '').replace(/,/g, ''));
    const adjustmentNum = parseFloat(String(next.faceValueAdjustment || '').replace(/,/g, ''));
    if (Number.isFinite(baseFaceNum)) {
      const adjusted = Number.isFinite(adjustmentNum) ? baseFaceNum + adjustmentNum : baseFaceNum;
      next.adjustedFaceValue = adjusted.toString();
    } else {
      next.adjustedFaceValue = '';
    }
    setFormLeg1(withAccruedInterestMoney(next));
  };
  const handleLeg2Change = (e) => {
    const { name, value } = e.target;
    // Reject out-of-range / malformed dates (e.g. a 5-digit year).
    if ((name === 'tradeDate' || name === 'valueDate') && !isDateInRange(value)) {
      return;
    }
    // Make calculated fields read-only from Leg 2 UI
    const readOnlyFields = [
      'isin', 'settlementAmount', 'faceValue', 'yieldRate', 
      'adjustedFaceValue',
      'dirtyPrice', 'cleanPrice', 'accruedInterestPer100',
      'issueDate', 'maturityDate', 'couponDate1', 'couponDate2', 'couponRate',
      'tradeDate' // Auto-populated from Leg 1
    ];
    if (readOnlyFields.includes(name)) {
      return; // ignore edits
    }
    
    // Holiday validation for valueDate
    if (name === 'valueDate') {
      if (value) {
        const currency = formLeg2.currency || 'LKR';
        checkHolidayForCurrency(value, currency).then(holidayCheck => {
          if (holidayCheck.isHoliday) {
            setLeg2ValueDateHolidayError(formatHolidayMessage(holidayCheck, 'Value Date'));
          } else {
            setLeg2ValueDateHolidayError('');
          }
        });
      } else {
        setLeg2ValueDateHolidayError('');
      }
    }
    
    // Re-validate valueDate when currency changes
    if (name === 'currency') {
      const currency = value || 'LKR';
      if (formLeg2.valueDate) {
        checkHolidayForCurrency(formLeg2.valueDate, currency).then(holidayCheck => {
          if (holidayCheck.isHoliday) {
            setLeg2ValueDateHolidayError(formatHolidayMessage(holidayCheck, 'Value Date'));
          } else {
            setLeg2ValueDateHolidayError('');
          }
        });
      }
    }
    
    setFormLeg2(prev => ({ ...prev, [name]: value }));
  };

  // Sell modal handlers
  const handleSellModalClose = () => {
    setShowSellModal(false);
    setSellDeals([]);
    setPortfolioFromSellModal(false);
    // Reset transaction type if no deals were selected
    if (sellDeals.length === 0) {
      setFormLeg1(prev => ({ ...prev, transactionType: '' }));
    }
  };

  const handleSellModalConfirm = (data) => {
    console.log('Sell modal confirmed with data:', data);
    
    if (data.deals && data.deals.length > 0) {
      // Update Leg 1 with sell data
      const totalSellAmount = Number(data.updateFaceValue) ||
        data.deals.reduce((sum, deal) => sum + (Number(deal.amountToSell) || 0), 0);
      setFormLeg1(prev => ({
        ...prev,
        faceValue: totalSellAmount.toString(),
        adjustedFaceValue: totalSellAmount.toString(),
        faceValueAdjustment: '',
        portfolio: data.portfolio || prev.portfolio,
        transactionType: 'Sell', // Ensure transaction type is set to Sell
        // Keep other fields as they were
      }));
      
      // Store sell deals for reference
      setSellDeals(data.deals);
      setPortfolioFromSellModal(data.portfolio);
      
      // Close modal
      setShowSellModal(false);
      
      console.log('Updated Leg 1 with sell data:', {
        faceValue: totalSellAmount,
        portfolio: data.portfolio,
        deals: data.deals
      });
    }
  };

  const handleSellModalPortfolioChange = (portfolio) => {
    setPortfolioFromSellModal(portfolio);
  };

  const resolveBuybackSettlementMode = (selectedCodeOrMethod) => {
    const opt = (settlementModes || []).find(m => m.value === selectedCodeOrMethod);
    return opt?.paymentMethod || selectedCodeOrMethod;
  };

  const toInputDate = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) {
      const asString = String(value);
      return asString.includes('T') ? asString.split('T')[0] : asString;
    }
    return d.toISOString().split('T')[0];
  };

  const loadDealIntoForm = (deal, mode = 'view') => {
    const leg1 = {
      ...initialFormState,
      tradeDate: toInputDate(deal.leg1_trade_date),
      transactionType: deal.leg1_transaction_type || '',
      counterparty: deal.leg1_counterparty || '',
      broker: deal.leg1_broker || '',
      portfolio: deal.leg1_portfolio || '',
      strategy: deal.leg1_strategy || '',
      custodian: deal.leg1_custodian || '',
      settlementMode: deal.leg1_settlement_mode || '',
      brokerage: deal.leg1_brokerage || '',
      isin: deal.leg1_isin || '',
      valueDate: toInputDate(deal.leg1_value_date),
      yieldRate: deal.leg1_yield_rate || '',
      settlementAmount: deal.leg1_settlement_amount || '',
      interestRate: deal.leg1_interest_rate || '',
      issueDate: toInputDate(deal.issue_date),
      maturityDate: toInputDate(deal.maturity_date),
      couponRate: deal.coupon_rate || '',
      couponDate1: deal.coupon_date1 || '',
      couponDate2: deal.coupon_date2 || '',
      accruedInterestPer100: deal.leg1_accrued_interest || '',
      cleanPrice: deal.leg1_clean_price || '',
      dirtyPrice: deal.leg1_dirty_price || '',
      faceValue: deal.leg1_face_value || '',
      adjustedFaceValue: deal.leg1_adjusted_face_value || deal.leg1_face_value || '',
      faceValueAdjustment: (() => {
        const base = parseFloat(deal.leg1_face_value);
        const adj = parseFloat(deal.leg1_adjusted_face_value);
        if (!Number.isFinite(base) || !Number.isFinite(adj)) return '';
        const diff = adj - base;
        if (Math.abs(diff) < 0.0001) return '';
        return diff.toString();
      })(),
      currency: deal.leg1_currency || 'LKR',
      fundMovement: String(deal.fund_movement || 'no').toLowerCase() === 'yes' ? 'yes' : 'no'
    };

    const leg2 = {
      ...initialFormState,
      tradeDate: toInputDate(deal.leg2_trade_date || deal.leg1_trade_date),
      transactionType: deal.leg2_transaction_type || '',
      counterparty: deal.leg2_counterparty || '',
      broker: deal.leg2_broker || deal.leg1_broker || '',
      portfolio: deal.leg2_portfolio || '',
      strategy: deal.leg2_strategy || '',
      custodian: deal.leg2_custodian || '',
      settlementMode: deal.leg2_settlement_mode || '',
      isin: deal.leg2_isin || deal.leg1_isin || '',
      valueDate: toInputDate(deal.leg2_value_date),
      yieldRate: deal.leg2_yield_rate || '',
      settlementAmount: deal.leg2_settlement_amount || '',
      issueDate: toInputDate(deal.issue_date),
      maturityDate: toInputDate(deal.maturity_date),
      couponRate: deal.coupon_rate || '',
      couponDate1: deal.coupon_date1 || '',
      couponDate2: deal.coupon_date2 || '',
      accruedInterestPer100: deal.leg2_accrued_interest || '',
      cleanPrice: deal.leg2_clean_price || '',
      dirtyPrice: deal.leg2_dirty_price || '',
      faceValue: deal.leg2_face_value || deal.leg1_face_value || '',
      adjustedFaceValue:
        deal.leg2_adjusted_face_value ||
        deal.leg1_adjusted_face_value ||
        deal.leg2_face_value ||
        deal.leg1_face_value ||
        '',
      faceValueAdjustment: (() => {
        const base = parseFloat(deal.leg2_face_value || deal.leg1_face_value);
        const adj = parseFloat(
          deal.leg2_adjusted_face_value || deal.leg1_adjusted_face_value || deal.leg2_face_value || deal.leg1_face_value
        );
        if (!Number.isFinite(base) || !Number.isFinite(adj)) return '';
        const diff = adj - base;
        if (Math.abs(diff) < 0.0001) return '';
        return diff.toString();
      })(),
      currency: deal.leg2_currency || 'LKR',
      brokerage: deal.leg2_brokerage || '',
      fundMovement: String(deal.fund_movement || 'no').toLowerCase() === 'yes' ? 'yes' : 'no'
    };

    setFormLeg1(withAccruedInterestMoney(leg1));
    setFormLeg2(withAccruedInterestMoney(leg2));

    // Restore the buy-deal allocations that were saved with the deal so a Sell
    // Leg 1 keeps its allocation context when edited and re-submitted.
    let restoredSellDeals = [];
    if (deal.sell_deal_allocations) {
      try {
        const parsed =
          typeof deal.sell_deal_allocations === 'string'
            ? JSON.parse(deal.sell_deal_allocations)
            : deal.sell_deal_allocations;
        if (Array.isArray(parsed)) {
          restoredSellDeals = parsed
            .map((a) => ({
              deal_number: a.deal_number || a.buy_deal_number,
              amountToSell: Number(a.amountToSell) || 0
            }))
            .filter((a) => a.deal_number && a.amountToSell > 0);
        }
      } catch (e) {
        restoredSellDeals = [];
      }
    }
    setSellDeals(restoredSellDeals);

    setEditingBuybackDealId(deal.id);
    setEditingBuybackDealStatus(deal.deal_status || '');
    setIsBuybackViewMode(mode === 'view');
    setIsBuybackEditMode(mode === 'edit');
    setSuccess(
      mode === 'edit'
        ? `Editing rejected deal ${deal.deal_number}. Update fields and click save to re-submit.`
        : `Viewing deal ${deal.deal_number}.`
    );
    setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCombinedSubmit = async () => {
    setSubmitting(true);
    setError('');
    setSuccess('');

    if (isBuybackViewMode) {
      setError('This deal is in view mode. Use Edit on a rejected deal to make changes.');
      setSubmitting(false);
      return;
    }

    // A Sell Leg 1 must be backed by buy-deal allocations from the sell holdings
    // modal (applies in both create and rejected-edit flows). Without them the
    // approval step can't deduct against specific buy deals.
    if (String(formLeg1.transactionType || '').toLowerCase() === 'sell' && sellDeals.length === 0) {
      setError('Leg 1 (Sell): please allocate buy deals from the sell holdings modal before saving.');
      setSubmitting(false);
      return;
    }

    // Validate available balance for sell legs only.
    // Skip validation when editing an existing deal — the balance was already consumed by the original deal.
    if (!isBuybackEditMode) {
      if (String(formLeg1.transactionType || '').toLowerCase() === 'sell' && Number(formLeg1.faceValue || 0) > 0) {
        // For Sell+Buyback flow, use exact selected deals from the sell modal to avoid report-level false negatives.
        if (sellDeals.length > 0) {
          const toNum = (v) => {
            if (v === null || v === undefined || v === '') return NaN;
            const n = parseFloat(String(v).replace(/,/g, '').trim());
            return Number.isFinite(n) ? n : NaN;
          };
          // Prefer original remaining_face_value from modal rows; if only post-sell remainingFaceValue is available,
          // reconstruct original available as post-sell remaining + amountToSell.
          const availableFromSelectedDeals = sellDeals.reduce(
            (sum, d) => {
              const originalRemaining = toNum(d?.remaining_face_value);
              if (Number.isFinite(originalRemaining) && originalRemaining >= 0) {
                return sum + originalRemaining;
              }
              const reconstructed = toNum(d?.remainingFaceValue) + (toNum(d?.amountToSell) || 0);
              if (Number.isFinite(reconstructed) && reconstructed >= 0) {
                return sum + reconstructed;
              }
              const fallbackFace = toNum(d?.face_value ?? d?.faceValue ?? 0) || 0;
              return sum + fallbackFace;
            },
            0
          );
          // Validate against the exact selected sell allocations from the modal.
          // This avoids false failures when the displayed leg face value is rounded to the nearest 100.
          const requestedLeg1 = sellDeals.reduce(
            (sum, d) => sum + (toNum(d?.amountToSell) || 0),
            0
          );
          if (requestedLeg1 > availableFromSelectedDeals + 0.0001) {
            setError(
              `Balance validation failed for Leg 1: Insufficient available balance. Available: ${availableFromSelectedDeals.toLocaleString()}, Requested: ${requestedLeg1.toLocaleString()}`
            );
            setSubmitting(false);
            return;
          }
        } else {
          setError('Balance validation failed for Leg 1: Please select sell deals from the sell modal first.');
          setSubmitting(false);
          return;
        }
      }

      // Leg 2 should only consume balance from the existing portfolio when Leg 1
      // is itself a Sell. In the Buy-first flow (Leg 1 = Buy, Leg 2 = Sell),
      // the Leg 2 Sell deducts from the Leg 1 Buy being created in this same
      // deal, so the existing gsecReportData is not the right source of truth
      // and would incorrectly fail with "ISIN not found in GSEC report data".
      const leg1IsSell = String(formLeg1.transactionType || '').toLowerCase() === 'sell';
      const leg2IsSell = String(formLeg2.transactionType || '').toLowerCase() === 'sell';

      if (leg1IsSell && leg2IsSell && Number(formLeg2.faceValue || 0) > 0 && gsecReportData.length > 0) {
        const leg2Validation = validateAvailableBalance(
          formLeg2.isin,
          formLeg2.faceValue,
          'Buyback',
          gsecReportData
        );

        if (!leg2Validation.isValid) {
          setError(`Balance validation failed for Leg 2: ${leg2Validation.message}`);
          setSubmitting(false);
          return;
        }
      }

      console.log('Balance validation passed for required sell leg(s)');
    }

    const leg1Form = withAccruedInterestMoney(formLeg1);
    const leg2Form = withAccruedInterestMoney(formLeg2);

    // Map frontend fields to DB columns for each leg
    const leg1 = {
      isin: formLeg1.isin,
      tradeDate: formLeg1.tradeDate,
      tradeType: formLeg1.tradeType,
      transactionType: formLeg1.transactionType,
      valueDate: formLeg1.valueDate,
      counterparty: formLeg1.counterparty,
      broker: formLeg1.broker,
      portfolio: formLeg1.portfolio,
      strategy: formLeg1.strategy,
      custodian: formLeg1.custodian,
      settlementMode: resolveBuybackSettlementMode(formLeg1.settlementMode),
      brokerage: formLeg1.brokerage,
      faceValue: leg1Form.faceValue,
      adjustedFaceValue: leg1Form.adjustedFaceValue,
      // Store the per-100 accrued figure, NOT the LKR money amount. The
      // buyback_deals leg*_accrued_interest columns are DECIMAL(10,4) and every
      // reader (edit-load above, confirmation letters, premature-maturity
      // recalc) treats them as per-100; sending the money amount overflowed the
      // column and clamped to the 999999.9999 sentinel. Ledger postings are
      // unaffected - they recompute accrued from Face x (Dirty - Clean) / 100.
      accruedInterest: leg1Form.accruedInterestPer100,
      cleanPrice: leg1Form.cleanPrice,
      dirtyPrice: leg1Form.dirtyPrice,
             yield: leg1Form.yieldRate,
       settlementAmount: leg1Form.settlementAmount,
       interestRate: leg1Form.interestRate,
       currency: leg1Form.currency
    };
    const leg2 = {
      isin: leg2Form.isin,
      tradeDate: leg2Form.tradeDate, // Auto-populated from Leg 1
      tradeType: leg2Form.tradeType,
      transactionType: leg2Form.transactionType, // Fixed: Now correctly sends "Buy" for Leg 2
      valueDate: leg2Form.valueDate,
      counterparty: leg2Form.counterparty,
      faceValue: leg2Form.faceValue,
      adjustedFaceValue: leg2Form.adjustedFaceValue,
      // Per-100 figure, not LKR money - see leg1 comment above.
      accruedInterest: leg2Form.accruedInterestPer100,
      cleanPrice: leg2Form.cleanPrice,
      dirtyPrice: leg2Form.dirtyPrice,
      portfolio: leg2Form.portfolio,
      strategy: leg2Form.strategy,
      settlementAmount: leg2Form.settlementAmount,
      yield: leg2Form.yieldRate,
      couponRate: leg2Form.couponRate,
      issueDate: leg2Form.issueDate,
      maturityDate: leg2Form.maturityDate,
      couponDate1: leg2Form.couponDate1,
      couponDate2: leg2Form.couponDate2,
      settlementMode: resolveBuybackSettlementMode(leg2Form.settlementMode),
      currency: leg2Form.currency,
      broker: leg2Form.broker,
      custodian: leg2Form.custodian,
      // Additional fields for GSec creation
      brokerage: leg2Form.brokerage || 0,
      accruedInterestCalculation: leg2Form.accruedInterestCalculation,
      accruedInterestSixDecimals: leg2Form.accruedInterestSixDecimals,
      accruedInterestFor100: leg2Form.accruedInterestFor100,
      accruedInterestBase: leg2Form.accruedInterestBase,
      nextCouponDate: leg2Form.nextCouponDate,
      lastCouponDate: leg2Form.lastCouponDate,
      numberOfDaysInterestAccrued: leg2Form.numberOfDaysInterestAccrued,
      numberOfDaysForCouponPeriod: leg2Form.numberOfDaysForCouponPeriod,
      couponInterest: leg2Form.couponInterest
    };

    // Counterparty is compulsory on both legs - never save a buyback without one.
    if (!String(leg1.counterparty || '').trim()) {
      setError('Leg 1: Please select a counterparty before saving.');
      setSubmitting(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (!String(leg2.counterparty || '').trim()) {
      setError('Leg 2: Please select a counterparty before saving.');
      setSubmitting(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Read-only/computed values must be populated before saving. If the reverse/
    // forward price calculation never ran, these come through empty and would
    // persist as 0 / garbage on the leg, so block the save with a clear message.
    const blankPositive = (v) => {
      if (v === '' || v === null || v === undefined) return true;
      const n = parseFloat(String(v).replace(/,/g, ''));
      return !Number.isFinite(n) || n <= 0;
    };
    const blankNumber = (v) => {
      if (v === '' || v === null || v === undefined) return true;
      return !Number.isFinite(parseFloat(String(v).replace(/,/g, '')));
    };
    const requiredComputed = [
      ['Clean Price', 'cleanPrice'],
      ['Dirty Price', 'dirtyPrice'],
      ['Settlement Amount', 'settlementAmount'],
      ['Yield', 'yield']
    ];
    for (const [label, key] of requiredComputed) {
      if (blankPositive(leg1[key])) {
        setError(`Leg 1: ${label} is empty. Computed values must be calculated before saving.`);
        setSubmitting(false);
        return;
      }
      if (blankPositive(leg2[key])) {
        setError(`Leg 2: ${label} is empty. Computed values must be calculated before saving.`);
        setSubmitting(false);
        return;
      }
    }
    if (blankNumber(leg1.accruedInterest)) {
      setError('Leg 1: Accrued Interest is empty. Computed values must be calculated before saving.');
      setSubmitting(false);
      return;
    }
    if (blankNumber(leg2.accruedInterest)) {
      setError('Leg 2: Accrued Interest is empty. Computed values must be calculated before saving.');
      setSubmitting(false);
      return;
    }

    try {
      // Sell-modal allocation sum is authoritative; sync leg faces to match before save.
      const allocSum = sellDeals.reduce((s, d) => s + (Number(d.amountToSell) || 0), 0);
      const scaleSettlement = (settlement, oldFace) => {
        const s = parseFloat(settlement);
        const oldF = parseFloat(oldFace);
        if (!Number.isFinite(s) || !Number.isFinite(oldF) || oldF <= 0 || !(allocSum > 0)) return settlement;
        if (Math.abs(oldF - allocSum) < 0.0001) return settlement;
        return (Math.round((s * allocSum / oldF) * 100) / 100).toFixed(2);
      };
      if (allocSum > 0 && String(formLeg1.transactionType || '').toLowerCase() === 'sell') {
        const oldLeg1Face = parseFloat(leg1Form.adjustedFaceValue || leg1Form.faceValue || 0) || allocSum;
        const oldLeg2Face = parseFloat(leg2Form.adjustedFaceValue || leg2Form.faceValue || 0) || oldLeg1Face;
        leg1.faceValue = allocSum.toString();
        leg1.adjustedFaceValue = allocSum.toString();
        leg1.settlementAmount = scaleSettlement(leg1.settlementAmount, oldLeg1Face);
        leg2.faceValue = allocSum.toString();
        leg2.adjustedFaceValue = allocSum.toString();
        leg2.settlementAmount = scaleSettlement(leg2.settlementAmount, oldLeg2Face);
      }
      const normalizedSellDeals = sellDeals;

      // Extract source buy deal number from sellDeals for tracking
      const source_buy_deal_number = normalizedSellDeals.length > 0 ? normalizedSellDeals[0].deal_number : null;
      
      console.log('=== FRONTEND BUYBACK SUBMISSION DEBUG ===');
      console.log('leg1.transactionType:', leg1.transactionType);
      console.log('leg2.transactionType:', leg2.transactionType); // Added to debug
      console.log('sellDeals:', sellDeals);
      console.log('sellDeals length:', sellDeals.length);
      console.log('source_buy_deal_number:', source_buy_deal_number);
      
      const isRejectedEdit =
        isBuybackEditMode &&
        editingBuybackDealId &&
        String(editingBuybackDealStatus || '').toLowerCase() === 'rejected';

      const endpoint = isRejectedEdit
        ? `${API_BASE_URL}/buyback/${editingBuybackDealId}`
        : `${API_BASE_URL}/buyback`;

      const response = await fetch(endpoint, {
        method: isRejectedEdit ? 'PUT' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ 
          leg1, 
          leg2, 
          fund_movement: String(formLeg1.fundMovement || 'no').toLowerCase() === 'yes' ? 'yes' : 'no',
          sellDeals: normalizedSellDeals,
          source_buy_deal_number: source_buy_deal_number,
          userId: getCurrentUserId()
        })
      });
      const result = await response.json();
      if (response.ok && result.success) {
        const dealId = result.data?.id || result.data?.deal_number || editingBuybackDealId;
        setCreatedBuyBackDealId(dealId);
        setShowBuyBackDocumentSection(true);
        setSuccess(
          (isRejectedEdit
            ? 'Rejected deal updated and re-submitted for verification successfully.'
            : `Both legs saved successfully! Deal Number: ${result.data.deal_number}`) +
            (result.limitWarning ? ' This deal exceeds your dealer limit — Middle Office has been notified.' : '') +
            (result.counterpartyLimitWarning
              ? ` ${result.counterpartyLimitWarning.message} Middle Office has been notified.`
              : '')
        );
        setFormLeg1(initialFormState);
        setFormLeg2(initialFormState);
        setIsBuybackViewMode(false);
        setIsBuybackEditMode(false);
        setEditingBuybackDealId(null);
        setEditingBuybackDealStatus('');
        // Clear sell deals after successful submission
        setSellDeals([]);
        setPortfolioFromSellModal(false);
        // Refresh the recent deals list
        fetchRecentDeals();
      } else {
        setError(result.error || 'Failed to save buyback legs');
      }
    } catch (err) {
      setError('Network or server error: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };


  return (
    <>
    <form onSubmit={(e) => e.preventDefault()}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2rem', justifyContent: 'center' }}>
        <div style={{ flex: 1, minWidth: 850, maxWidth: 4000 }}>
          <div className="transaction-page" style={{ padding: '1rem 2rem' }}>
            <h2>Gsec BuyBack - Leg 1</h2>
            <div className="form-grid">
              {/* Trade Date */}
              <div className="form-group">
                <label className="form-label">Trade Date</label>
                <input 
                  type="date" 
                  name="tradeDate" 
                  value={formLeg1.tradeDate} 
                  onChange={handleLeg1Change} 
                  min={DATE_INPUT_MIN}
                  max={DATE_INPUT_MAX}
                  className={`form-control ${leg1TradeDateHolidayError ? 'is-invalid' : ''}`}
                  required 
                />
                {leg1TradeDateHolidayError && (
                  <div className="text-danger" style={{ fontSize: '12px', marginTop: '4px' }}>
                    {leg1TradeDateHolidayError}
                  </div>
                )}
              </div>
                {/* ISIN */}
                <div className="form-group">
                  <label className="form-label" htmlFor="leg1-isin">ISIN</label>
                  <select id="leg1-isin" name="isin" className="form-control" value={formLeg1.isin} onChange={handleLeg1Change} required>
                    <option value="">{loadingIsins ? 'Loading ISINs...' : 'Select ISIN'}</option>
                    {memoIsins.map((isin, idx) => (
                      <option key={`${isin.isin_number || 'isin'}-${isin.id || idx}`} value={isin.isin_number}>
                        {isin.isin_number} - {isin.isin_issuer}
                      </option>
                    ))}
                  </select>
                </div>
              {/* Value Date */}
              <div className="form-group">
                <label className="form-label">Value Date</label>
                <input 
                  type="date" 
                  name="valueDate" 
                  value={formLeg1.valueDate} 
                  onChange={handleLeg1Change} 
                  min={DATE_INPUT_MIN}
                  max={DATE_INPUT_MAX}
                  className={`form-control ${leg1ValueDateHolidayError ? 'is-invalid' : ''}`}
                  required 
                />
                {leg1ValueDateHolidayError && (
                  <div className="text-danger" style={{ fontSize: '12px', marginTop: '4px' }}>
                    {leg1ValueDateHolidayError}
                  </div>
                )}
              </div>
              {/* Yield */}
              <div className="form-group">
                <label className="form-label">Yield (%)</label>
                <input type="number" step="0.000001" name="yieldRate" value={formLeg1.yieldRate} onChange={handleLeg1Change} className="form-control" required />
              </div>
              {/* Settlement Amount */}
              <div className="form-group">
                <label className="form-label">Settlement Amount (LKR)</label>
                <input
                  type="text"
                  inputMode="decimal"
                  name="settlementAmount"
                  value={formatAmountInput(formLeg1.settlementAmount)}
                  onChange={handleLeg1Change}
                  className="form-control"
                  required
                />
                {lastModifiedField === 'settlementAmount' && (
                  <small className="form-text text-info">
                    <i className="fas fa-info-circle"></i> This field is driving the calculation - Face Value will be calculated
                  </small>
                )}
              </div>
              {/* Transaction Type */}
              <div className="form-group">
                <label className="form-label">Transaction Type</label>
                <select
                  name="transactionType"
                  value={formLeg1.transactionType}
                  onChange={handleLeg1Change}
                  className="form-control"
                  required
                  style={{ ...getDerivedFieldStyle(formLeg1.transactionType), fontWeight: formLeg1.transactionType ? 600 : 400 }}
                >
                  <option value="">Select Transaction Type</option>
                  <option value="Buy">Buy</option>
                  <option value="Sell">Sell</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Fund Movement</label>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center', minHeight: '38px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <input
                      type="radio"
                      name="fundMovement"
                      value="yes"
                      checked={formLeg1.fundMovement === 'yes'}
                      onChange={handleLeg1Change}
                    />
                    Yes
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <input
                      type="radio"
                      name="fundMovement"
                      value="no"
                      checked={formLeg1.fundMovement !== 'yes'}
                      onChange={handleLeg1Change}
                    />
                    No
                  </label>
                </div>
              </div>

              {/* Counterparty */}
              <div className="form-group">
                <label className="form-label">Counterparty</label>
                <select name="counterparty" value={formLeg1.counterparty} onChange={handleLeg1Change} className="form-control" required disabled={loadingCounterparties}>
                  <option value="">{loadingCounterparties ? 'Loading...' : 'Select Counterparty'}</option>
                  {(() => {
                    const individual = counterparties.filter(cp => cp.type === 'individual');
                    const joint = counterparties.filter(cp => cp.type === 'joint');
                    const corporate = counterparties.filter(cp => cp.type === 'corporate');
                    return (
                      <>
                        {individual.length > 0 && (
                          <optgroup label="Individual" className="counterparty-individual">
                            {individual.map(cp => (
                              <option key={cp.unique_id} value={cp.unique_id} className="counterparty-individual">
                                {cp.short_name}{cp.long_name ? ` - ${cp.long_name}` : ''}
                              </option>
                            ))}
                          </optgroup>
                        )}
                        {joint.length > 0 && (
                          <optgroup label="Joint" className="counterparty-joint">
                            {joint.map(cp => (
                              <option key={cp.unique_id} value={cp.unique_id} className="counterparty-joint">
                                {cp.short_name}{cp.long_name ? ` - ${cp.long_name}` : ''}
                              </option>
                            ))}
                          </optgroup>
                        )}
                        {corporate.length > 0 && (
                          <optgroup label="Corporate" className="counterparty-corporate">
                            {corporate.map(cp => (
                              <option key={cp.unique_id} value={cp.unique_id} className="counterparty-corporate">
                                {cp.short_name}{cp.long_name ? ` - ${cp.long_name}` : ''}
                              </option>
                            ))}
                          </optgroup>
                        )}
                      </>
                    );
                  })()}
                </select>
              </div>

              {/* Broker */}
              <div className="form-group">
                <label className="form-label">Broker</label>
                <select name="broker" value={formLeg1.broker} onChange={handleLeg1Change} className="form-control" disabled={loadingBrokers}>
                  <option value="">{loadingBrokers ? 'Loading...' : 'Select Broker'}</option>
                  {brokers.map(broker => (
                    <option key={broker.id} value={broker.id}>
                      {broker.broker_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Portfolio */}
              <div className="form-group">
                <label className="form-label">Portfolio</label>
                <select name="portfolio" value={formLeg1.portfolio} onChange={handleLeg1Change} className="form-control" disabled={loadingPortfolios}>
                  <option value="">{loadingPortfolios ? 'Loading...' : 'Select Portfolio'}</option>
                  {portfolios.map(portfolio => (
                    <option key={portfolio.portfolio_id} value={portfolio.portfolio_id}>
                      {portfolio.portfolio_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Strategy */}
              <div className="form-group">
                <label className="form-label">Strategy</label>
                <select name="strategy" value={formLeg1.strategy} onChange={handleLeg1Change} className="form-control" disabled={loadingStrategies}>
                  <option value="">{loadingStrategies ? 'Loading...' : 'Select Strategy'}</option>
                  {strategies.map(strategy => (
                    <option key={strategy.strategy_id} value={strategy.strategy_id}>
                      {strategy.strategy_id}{strategy.strategy_type ? ` (${strategy.strategy_type})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Custodian */}
              <div className="form-group">
                <label className="form-label">Custodian</label>
                <select name="custodian" value={formLeg1.custodian} onChange={handleLeg1Change} className="form-control">
                  <option value="">Select Custodian</option>
                  {custodians.map(custodian => (
                    <option key={custodian} value={custodian}>
                      {custodian}
                    </option>
                  ))}
                </select>
              </div>

              {/* Settlement Mode */}
              <div className="form-group">
                <label className="form-label">Settlement Mode</label>
                <select name="settlementMode" value={formLeg1.settlementMode} onChange={handleLeg1Change} className="form-control">
                  <option value="">Select Settlement Mode</option>
                  {settlementModes.map(mode => (
                    <option key={mode.value} value={mode.value}>
                      {mode.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Brokerage */}
              <div className="form-group">
                <label className="form-label">Brokerage (%)</label>
                <input
                  type="number"
                  step="0.0001"
                  name="brokerage"
                  value={formLeg1.brokerage}
                  onChange={handleLeg1Change}
                  className="form-control"
                  readOnly={Boolean(formLeg1.broker)}
                />
              </div>

              {/* Interest Rate */}
              <div className="form-group">
                <label className="form-label">Interest Rate (%)</label>
                <input type="number" step="0.0001" name="interestRate" value={formLeg1.interestRate} onChange={handleLeg1Change} className="form-control" />
              </div>

              {/* Day Count for Interest Calculation */}
              <div className="form-group">
                <label className="form-label">Day Count</label>
                <select value={dayCount} onChange={(e) => setDayCount(e.target.value)} className="form-control">
                  <option value="365">365 Days</option>
                  <option value="364">364 Days</option>
                </select>
                <small className="form-text text-muted">Used for interest calculation: Days / Day Count</small>
              </div>

              {/* Read-only metadata from ISIN */}
              <div className="form-group">
                <label className="form-label">Issue Date</label>
                <input type="date" name="issueDate" value={formLeg1.issueDate} className="form-control" readOnly style={getDerivedFieldStyle(formLeg1.transactionType)} />
              </div>
              <div className="form-group">
                <label className="form-label">Maturity Date</label>
                <input type="date" name="maturityDate" value={formLeg1.maturityDate} className="form-control" readOnly style={getDerivedFieldStyle(formLeg1.transactionType)} />
              </div>
              <div className="form-group">
                <label className="form-label">Coupon Rate (%)</label>
                <input name="couponRate" value={formLeg1.couponRate} className="form-control" readOnly style={getDerivedFieldStyle(formLeg1.transactionType)} />
              </div>
              <div className="form-group">
                <label className="form-label">Coupon Month 1</label>
                <input name="couponDate1" value={formLeg1.couponDate1} className="form-control" readOnly style={getDerivedFieldStyle(formLeg1.transactionType)} />
              </div>
              <div className="form-group">
                <label className="form-label">Coupon Month 2</label>
                <input name="couponDate2" value={formLeg1.couponDate2} className="form-control" readOnly style={getDerivedFieldStyle(formLeg1.transactionType)} />
              </div>
              <div className="form-group">
                <label className="form-label">Currency</label>
                <input name="currency" value="LKR" className="form-control" readOnly style={getDerivedFieldStyle(formLeg1.transactionType)} />
              </div>

              {/* Derived values from reverse calc */}
              <div className="form-group">
                <label className="form-label">Accrued Interest (per 100)</label>
                <input name="accruedInterestPer100" value={formLeg1.accruedInterestPer100 || ''} className="form-control" readOnly style={getDerivedFieldStyle(formLeg1.transactionType)} />
              </div>
              <div className="form-group">
                <label className="form-label">Clean Price (per 100)</label>
                <input name="cleanPrice" value={formLeg1.cleanPrice || ''} className="form-control" readOnly style={getDerivedFieldStyle(formLeg1.transactionType)} />
              </div>
              <div className="form-group">
                <label className="form-label">Dirty Price (per 100)</label>
                <input name="dirtyPrice" value={formLeg1.dirtyPrice || ''} className="form-control" readOnly style={getDerivedFieldStyle(formLeg1.transactionType)} />
              </div>
              <div className="form-group">
                <label className="form-label">Face Value</label>
                <input
                  type="text"
                  inputMode="decimal"
                  name="faceValue"
                  value={formatAmountInput(formLeg1.adjustedFaceValue || formLeg1.faceValue || '')}
                  onChange={handleLeg1Change}
                  className="form-control"
                />
                {lastModifiedField === 'faceValue' && (
                  <small className="form-text text-info">
                    <i className="fas fa-info-circle"></i> This field is driving the calculation - Settlement Amount will be calculated
                  </small>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Face Value Adjustment (+/-)</label>
                <input
                  type="text"
                  inputMode="decimal"
                  name="faceValueAdjustment"
                  value={formatAmountInput(formLeg1.faceValueAdjustment || '')}
                  onChange={handleLeg1Change}
                  className="form-control"
                  placeholder="+100000.00 or -50000.00"
                />
              </div>
            </div>
          </div>
        </div>
        <div style={{ flex: 1, minWidth: 850, maxWidth: 4000 }}>
        <div className="transaction-page" style={{ padding: '1rem 2rem' }}>
            <h2>Leg 2</h2>
            <div className="form-grid">
              {/* Value Date */}
              <div className="form-group">
                <label className="form-label">Value Date</label>
                <input 
                  type="date" 
                  name="valueDate" 
                  value={formLeg2.valueDate} 
                  onChange={handleLeg2Change} 
                  min={DATE_INPUT_MIN}
                  max={DATE_INPUT_MAX}
                  className={`form-control ${leg2ValueDateHolidayError ? 'is-invalid' : ''}`}
                />
                {leg2ValueDateHolidayError && (
                  <div className="text-danger" style={{ fontSize: '12px', marginTop: '4px' }}>
                    {leg2ValueDateHolidayError}
                  </div>
                )}
              </div>

              {/* Yield (read-only, computed) */}
              <div className="form-group">
                <label className="form-label">Yield (%)</label>
                <input name="yieldRate" value={formLeg2.yieldRate || ''} className="form-control" readOnly style={getDerivedFieldStyle(formLeg2.transactionType)} />
              </div>

              {/* Settlement Amount (read-only, computed) */}
              <div className="form-group">
                <label className="form-label">Settlement Amount (LKR)</label>
                <input
                  name="settlementAmount"
                  value={formatAmountInput(formLeg2.settlementAmount || '')}
                  className="form-control"
                  readOnly
                  style={getDerivedFieldStyle(formLeg2.transactionType)}
                />
              </div>

              {/* Trade Date (auto-populated from Leg 1) */}
              <div className="form-group">
                <label className="form-label">Trade Date</label>
                <input type="date" name="tradeDate" value={formLeg2.tradeDate || ''} className="form-control" readOnly style={getDerivedFieldStyle(formLeg2.transactionType)} />
                <small className="form-text text-muted">
                  Automatically populated from Leg 1 trade date
                </small>
              </div>

              {/* Transaction Type (auto-switched from Leg 1) */}
              <div className="form-group">
                <label className="form-label">Transaction Type</label>
                <select
                  name="transactionType"
                  value={formLeg2.transactionType}
                  onChange={handleLeg2Change}
                  className="form-control"
                  required
                  style={{ ...getDerivedFieldStyle(formLeg2.transactionType), fontWeight: formLeg2.transactionType ? 600 : 400 }}
                >
                  <option value="">Select Transaction Type</option>
                  <option value="Buy">Buy</option>
                  <option value="Sell">Sell</option>
                </select>
                <small className="form-text text-muted">
                  Automatically switched based on Leg 1 selection
                </small>
              </div>

              {/* ISIN (read-only, mirrors Leg 1) */}
              <div className="form-group">
                <label className="form-label">ISIN</label>
                <input name="isin" value={formLeg2.isin || ''} className="form-control" readOnly style={getDerivedFieldStyle(formLeg2.transactionType)} />
              </div>

              {/* Interest Calculation Display */}
              {formLeg1.settlementAmount && formLeg1.interestRate && formLeg1.valueDate && formLeg2.valueDate && (
                <div className="form-group">
                  <label className="form-label">Interest Calculation</label>
                  <div className="alert alert-info" style={{ marginBottom: 0 }}>
                    <small>
                      <strong>Days:</strong> {calculateDaysBetween(formLeg1.valueDate, formLeg2.valueDate)} days<br/>
                      <strong>Interest:</strong> {formLeg1.settlementAmount} × {formLeg1.interestRate}% × ({calculateDaysBetween(formLeg1.valueDate, formLeg2.valueDate)}/{dayCount}) = {((parseFloat(formLeg1.settlementAmount) || 0) * (parseFloat(formLeg1.interestRate) || 0) / 100 * (calculateDaysBetween(formLeg1.valueDate, formLeg2.valueDate) / parseInt(dayCount))).toFixed(2)} LKR
                    </small>
                  </div>
                </div>
              )}

              {/* Face Value (read-only, mirrors Leg 1) */}
              <div className="form-group">
                <label className="form-label">Face Value</label>
                <input
                  name="faceValue"
                  value={formatAmountInput(formLeg2.adjustedFaceValue || formLeg2.faceValue || '')}
                  className="form-control"
                  readOnly
                  style={getDerivedFieldStyle(formLeg2.transactionType)}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Face Value Adjustment (+/-)</label>
                <input
                  name="faceValueAdjustment"
                  value={formatAmountInput(formLeg2.faceValueAdjustment || '')}
                  className="form-control"
                  readOnly
                  style={getDerivedFieldStyle(formLeg2.transactionType)}
                />
              </div>

              {/* Calculated fields from forward calculation */}
              <div className="form-group">
                <label className="form-label">Accrued Interest (per 100)</label>
                <input name="accruedInterestPer100" value={formLeg2.accruedInterestPer100 || ''} className="form-control" readOnly style={getDerivedFieldStyle(formLeg2.transactionType)} />
              </div>
              <div className="form-group">
                <label className="form-label">Accrued Interest (LKR)</label>
                <input name="accruedInterest" value={formLeg2.accruedInterest || ''} className="form-control" readOnly style={getDerivedFieldStyle(formLeg2.transactionType)} />
              </div>

              <div className="form-group">
                <label className="form-label">Clean Price (per 100)</label>
                <input name="cleanPrice" value={formLeg2.cleanPrice || ''} className="form-control" readOnly style={getDerivedFieldStyle(formLeg2.transactionType)} />
              </div>

              <div className="form-group">
                <label className="form-label">Dirty Price (per 100)</label>
                <input name="dirtyPrice" value={formLeg2.dirtyPrice || ''} className="form-control" readOnly style={getDerivedFieldStyle(formLeg2.transactionType)} />
              </div>

              {/* Counterparty */}
              <div className="form-group">
                <label className="form-label">Counterparty</label>
                <select name="counterparty" value={formLeg2.counterparty} onChange={handleLeg2Change} className="form-control" required disabled={loadingCounterparties}>
                  <option value="">{loadingCounterparties ? 'Loading...' : 'Select Counterparty'}</option>
                  {(() => {
                    const individual = counterparties.filter(cp => cp.type === 'individual');
                    const joint = counterparties.filter(cp => cp.type === 'joint');
                    const corporate = counterparties.filter(cp => cp.type === 'corporate');
                    return (
                      <>
                        {individual.length > 0 && (
                          <optgroup label="Individual" className="counterparty-individual">
                            {individual.map(cp => (
                              <option key={cp.unique_id} value={cp.unique_id} className="counterparty-individual">
                                {cp.short_name}{cp.long_name ? ` - ${cp.long_name}` : ''}
                              </option>
                            ))}
                          </optgroup>
                        )}
                        {joint.length > 0 && (
                          <optgroup label="Joint" className="counterparty-joint">
                            {joint.map(cp => (
                              <option key={cp.unique_id} value={cp.unique_id} className="counterparty-joint">
                                {cp.short_name}{cp.long_name ? ` - ${cp.long_name}` : ''}
                              </option>
                            ))}
                          </optgroup>
                        )}
                        {corporate.length > 0 && (
                          <optgroup label="Corporate" className="counterparty-corporate">
                            {corporate.map(cp => (
                              <option key={cp.unique_id} value={cp.unique_id} className="counterparty-corporate">
                                {cp.short_name}{cp.long_name ? ` - ${cp.long_name}` : ''}
                              </option>
                            ))}
                          </optgroup>
                        )}
                      </>
                    );
                  })()}
                </select>
              </div>

              {/* Portfolio */}
              <div className="form-group">
                <label className="form-label">Portfolio</label>
                <select name="portfolio" value={formLeg2.portfolio} onChange={handleLeg2Change} className="form-control" disabled={loadingPortfolios}>
                  <option value="">{loadingPortfolios ? 'Loading...' : 'Select Portfolio'}</option>
                  {portfolios.map(portfolio => (
                    <option key={portfolio.portfolio_id} value={portfolio.portfolio_id}>
                      {portfolio.portfolio_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Strategy */}
              <div className="form-group">
                <label className="form-label">Strategy</label>
                <select name="strategy" value={formLeg2.strategy} onChange={handleLeg2Change} className="form-control" disabled={loadingStrategies}>
                  <option value="">{loadingStrategies ? 'Loading...' : 'Select Strategy'}</option>
                  {strategies.map(strategy => (
                    <option key={strategy.strategy_id} value={strategy.strategy_id}>
                      {strategy.strategy_id}{strategy.strategy_type ? ` (${strategy.strategy_type})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              {/* Custodian */}
              <div className="form-group">
                <label className="form-label">Custodian</label>
                <select name="custodian" value={formLeg2.custodian} onChange={handleLeg2Change} className="form-control">
                  <option value="">Select Custodian</option>
                  {custodians.map(custodian => (
                    <option key={custodian} value={custodian}>
                      {custodian}
                    </option>
                  ))}
                </select>
              </div>

              {/* Settlement Mode */}
              <div className="form-group">
                <label className="form-label">Settlement Mode</label>
                <select name="settlementMode" value={formLeg2.settlementMode} onChange={handleLeg2Change} className="form-control">
                  <option value="">Select Settlement Mode</option>
                  {settlementModes.map(mode => (
                    <option key={mode.value} value={mode.value}>
                      {mode.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div style={{ marginTop: 32, textAlign: 'center' }}>
        <button type="button" className="btn btn-primary" onClick={handleCombinedSubmit} disabled={submitting}>
          {submitting
            ? 'Submitting...'
            : isBuybackEditMode
              ? 'Update & Re-submit for Verification'
              : 'Save & Send for Verification (Both Legs)'}
        </button>
        <button 
          type="button" 
          className="btn btn-secondary" 
          style={{ marginLeft: 16 }}
          onClick={() => setShowBlotter(!showBlotter)}
        >
          {showBlotter ? 'Hide' : 'Show'} Recent Deals
        </button>
        {error && <div className="alert alert-danger" style={{ marginTop: 16 }}>{error}</div>}
        {success && <div className="alert alert-success" style={{ marginTop: 16 }}>{success}</div>}
        
        {/* Balance Validation Display */}
        {!balanceValidation.isValid && (
          <div className="alert alert-warning" style={{ marginTop: 16 }}>
            <strong>Balance Validation:</strong> {balanceValidation.message}
          </div>
        )}
        
        {balanceValidation.isValid && balanceValidation.message && (
          <div className="alert alert-info" style={{ marginTop: 16 }}>
            <strong>Balance Status:</strong> {balanceValidation.message}
          </div>
        )}
      </div>

      {/* Recent Buyback Deals Blotter */}
      {showBlotter && (
        <div style={{ marginTop: 40, padding: '20px', border: '1px solid #ddd', borderRadius: '8px' }}>
          <RecentDealsDateFilter
            title="Recent Buyback Deals"
            filterDateYmd={recentDealsFilterDate}
            onFilterDateChange={setRecentDealsFilterDate}
            onRefresh={fetchRecentDeals}
            refreshing={loadingDeals || recentDealsFilterLoading}
          />

          {loadingDeals ? (
            <div style={{ textAlign: 'center', padding: 20 }}>Loading recent deals...</div>
          ) : filteredRecentDeals.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 20, color: '#666' }}>
              No buyback deals found for {recentDealsFilterDate || 'the selected date'}.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="table table-striped table-hover">
                <thead className="thead-dark">
                  <tr>
                    <th>Deal #</th>
                    <th>Created</th>
                    <th>ISIN</th>
                    <th>Status</th>
                    <th>Leg 1</th>
                    <th>Leg 2</th>
                    <th>Face Value</th>
                    <th>Leg 1 Settlement</th>
                    <th>Leg 2 Settlement</th>
                    <th>Interest Rate</th>
                    <th>Created By</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRecentDeals.map((deal, index) => (
                    <tr key={deal.id} style={{ fontSize: '0.9em' }}>
                      <td>
                        <strong style={{ color: '#007bff' }}>{deal.deal_number}</strong>
                      </td>
                      <td>
                        {new Date(deal.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                      <td>
                        <span style={{ fontFamily: 'monospace', fontSize: '0.85em' }}>
                          {deal.leg1_isin}
                        </span>
                      </td>
                      <td>
                        <span 
                          className={`badge ${
                            deal.deal_status === 'Approved' ? 'badge-success' :
                            deal.deal_status === 'Verified' ? 'badge-info' :
                            deal.deal_status === 'Pending_Verification' ? 'badge-warning' :
                            deal.deal_status === 'Rejected' ? 'badge-danger' :
                            'badge-secondary'
                          }`}
                        >
                          {deal.deal_status.replace('_', ' ')}
                        </span>
                      </td>
                      <td>
                        <div style={{ fontSize: '0.8em' }}>
                          <div><strong>{deal.leg1_transaction_type}</strong></div>
                          <div>{deal.leg1_trade_date}</div>
                          <div>Yield: {parseFloat(deal.leg1_yield_rate).toFixed(3)}%</div>
                        </div>
                      </td>
                      <td>
                        <div style={{ fontSize: '0.8em' }}>
                          <div><strong>{deal.leg2_transaction_type}</strong></div>
                          <div>{deal.leg2_trade_date}</div>
                          <div>Yield: {parseFloat(deal.leg2_yield_rate).toFixed(3)}%</div>
                        </div>
                      </td>
                      <td>
                        <div style={{ textAlign: 'right', fontFamily: 'monospace' }}>
                          {parseFloat(deal.leg1_face_value).toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                        </div>
                      </td>
                      <td>
                        <div style={{ textAlign: 'right', fontFamily: 'monospace' }}>
                          {parseFloat(deal.leg1_settlement_amount).toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                        </div>
                      </td>
                      <td>
                        <div style={{ textAlign: 'right', fontFamily: 'monospace' }}>
                          {parseFloat(deal.leg2_settlement_amount).toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}
                        </div>
                      </td>
                      <td>
                        <div style={{ textAlign: 'center', fontWeight: 'bold', color: '#28a745' }}>
                          {parseFloat(deal.leg1_interest_rate).toFixed(2)}%
                        </div>
                      </td>
                      <td>
                        {deal.created_by_name || 'System'}
                      </td>
                      <td>
                        <button
                          className="btn btn-sm btn-info"
                          onClick={() => loadDealIntoForm(deal, 'view')}
                          title="View deal"
                        >
                          View
                        </button>
                        <button
                          className="btn btn-sm btn-warning"
                          onClick={() => loadDealIntoForm(deal, 'edit')}
                          disabled={String(deal.deal_status || '').toLowerCase() !== 'rejected'}
                          style={{
                            marginLeft: '5px',
                            opacity: String(deal.deal_status || '').toLowerCase() === 'rejected' ? 1 : 0.5,
                            cursor: String(deal.deal_status || '').toLowerCase() === 'rejected' ? 'pointer' : 'not-allowed'
                          }}
                          title="Edit rejected deal"
                        >
                          Edit
                        </button>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => {
                            setSelectedBuyBackDealForDocuments(deal);
                            setShowBuyBackDocumentSection(false);
                          }}
                          style={{ marginLeft: '5px' }}
                          title="View/Upload Documents"
                        >
                          📎 Docs
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Sell Modal */}
      <GsecSellDealModal
        show={showSellModal}
        onClose={handleSellModalClose}
        onConfirm={handleSellModalConfirm}
        onPortfolioChange={handleSellModalPortfolioChange}
        isin={formLeg1.isin}
        gsecFaceValue={formLeg1.faceValue}
        cleanPrice={formLeg1.cleanPrice}
        isinData={memoIsins.find(i => i.isin_number === formLeg1.isin)}
        formValueDate={formLeg1.valueDate}
        strictFaceValueMatch={false}
      />
    </form>
    
    {/* Document Upload Section - Show after deal is created or when viewing existing deal */}
    {(showBuyBackDocumentSection && createdBuyBackDealId) || selectedBuyBackDealForDocuments ? (
      <div style={{ marginTop: '24px', width: '100%', padding: '20px', border: '1px solid #e0e0e0', borderRadius: '8px', background: '#fff' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h4 style={{ margin: 0 }}>
            Documents
            {(createdBuyBackDealId || selectedBuyBackDealForDocuments?.id || selectedBuyBackDealForDocuments?.deal_number) && (
              <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#666', marginLeft: '10px' }}>
                - Deal #: {createdBuyBackDealId || selectedBuyBackDealForDocuments?.id || selectedBuyBackDealForDocuments?.deal_number}
              </span>
            )}
          </h4>
          {selectedBuyBackDealForDocuments && (
            <button 
              className="btn btn-sm btn-secondary" 
              onClick={() => setSelectedBuyBackDealForDocuments(null)}
            >
              Close
            </button>
          )}
        </div>
        <DocumentUpload
          transactionType="buyback"
          transactionId={createdBuyBackDealId || selectedBuyBackDealForDocuments?.id || selectedBuyBackDealForDocuments?.deal_number}
          onUploadSuccess={(documents) => {
            setSuccess('Document(s) uploaded successfully');
            if (!selectedBuyBackDealForDocuments) {
              setShowBuyBackDocumentSection(false);
            }
          }}
          onUploadError={(error) => {
            setError('Failed to upload document: ' + (error.response?.data?.error || error.message));
          }}
          multiple={true}
        />
        <DocumentList
          transactionType="buyback"
          transactionId={createdBuyBackDealId || selectedBuyBackDealForDocuments?.id || selectedBuyBackDealForDocuments?.deal_number}
          onDocumentDeleted={() => {}}
        />
      </div>
    ) : null}
    </>
  );
};

export default FixedIncomeBuyBackPage;
